<template>
	<UiModalMobile
		v-model="modelValue"
		class="modal__mobile"
		:titleBtnSave="$t('Добавить')"
		:isDontNeedCloseWhenSave="true"
		@save="saveHandler"
		:is-loading-btn-save="props.isLoading"
		:z-index="zIndex"
	>
		<TypographyKTypographyBody2
			color="var(--color_font_black_wite)"
			font-type="bold"
		>
			{{ $t('Добавить талон') }}
		</TypographyKTypographyBody2>
		<div class="wrapper">
			<div
				v-if="!loading.getTalonPhoto"
				class="talon-wrapper"
			>
				<div class="images">
					<div
						class="image"
						v-for="(talon, index) in tripTalonList"
						:key="talon?.id"
					>
						<UiImage
							lazy="lazy"
							:src="talon.urlPreview"
							@click.stop="openModalInFullSize(index)"
						/>
						<UiButtonIcon
							background="var(--white)"
							padding="4px"
							class="btn_remove"
							@click="deleteImageHandler(talon)"
						>
							<UiIcon
								name="remove"
								folder="button"
								width="19px"
								height="19px"
							/>
						</UiButtonIcon>
					</div>
				</div>
				<UiButtonFile
					:color="ButtonType.primary"
					:size="ButtonSizeType.small"
					:label="'Добавить фото'"
					:isMultiple="true"
					:width="'100%'"
					:is-loading="loading.uploadFile"
					@load="uploadNewFile"
				>
					<UiIcon
						name="camera"
						folder="cargos"
						width="20px"
						height="20px"
						color="var(--white)"
					/>
				</UiButtonFile>
			</div>
			<div
				v-else-if="false"
				class="notification-empty"
			>
				Толонов нет
			</div>
			<div
				v-else
				class="loader"
			>
				<UiLoader
					:circle-width="2"
					:radius="20"
					:color="'var(--primary)'"
				/>
			</div>
		</div>
		<template #custom-actions> </template>
	</UiModalMobile>
	<UiModalConfirm
		v-if="isOpenConfirmDeleteImageModal"
		v-model="isOpenConfirmDeleteImageModal"
		title="Вы уверены, что хотите удалить изображение?"
		:is-need-save-btn="true"
		@save="confirmDeleteImageHandler"
	/>
	<UiModalImagesModal
		v-if="isOpenModalFullScreenImage"
		:initial-index="initialIndexImage"
		v-model="isOpenModalFullScreenImage"
		:images="listImages"
	/>
</template>

<script setup lang="ts">
	import { TYPE_COMMENT } from '~/const/ui';
	import { getDateInLocaleString } from '~/helpers/date';
	import { VALIDATE_RULES, validator } from '~/helpers/validate';
	import { ButtonSizeType, ButtonType } from '~~/shared/interfaces/ui';
	import { useTripCustomsPhotoStore } from '../../../store/tripCustomsPhoto.store';
	import type { ITrip } from '~~/shared/interfaces/truck';
	import type { ITripTalon } from '~~/shared/interfaces/tripCustomsPhoto';

	const emits = defineEmits(['save']);
	const modelValue = defineModel('modelValue', { required: true, type: Boolean });
	const props = withDefaults(
		defineProps<{
			isLoading?: boolean;
			zIndex?: number;
			currentTrip: ITrip;
		}>(),
		{}
	);

	const loading = reactive({
		uploadFile: false,
		getTalonPhoto: false
	});
	const listImages = computed(() => {
		return (
			tripTalonList.value?.map((i) => {
				return { preview: i.urlPreview, url: i.urlOriginal };
			}) || []
		);
	});
	const initialIndexImage = ref<number | null>(null);
	const isOpenModalFullScreenImage = ref(false);
	const currentTalon = ref<any | null>(null);
	const isOpenConfirmDeleteImageModal = ref(false);

	const openModalInFullSize = (index: number) => {
		initialIndexImage.value = index;
		setTimeout(() => {
			isOpenModalFullScreenImage.value = true;
		});
	};

	const tripCustomsPhotoStore = useTripCustomsPhotoStore();
	const tripTalonList = ref<ITripTalon[]>([]);

	const deleteImageHandler = async (image: any) => {
		currentTalon.value = image;
		setTimeout(() => {
			isOpenConfirmDeleteImageModal.value = true;
		});
	};

	const confirmDeleteImageHandler = async () => {
		const saveId = currentTalon.value!.id;
		const response = await tripCustomsPhotoStore.tripCustomPhotoDelete({ id: saveId });

		if (response.status) {
			// driverRequestsStore.deleteCurrentCargoImage({ id: saveId });
			getTalonPhoto();
			isOpenConfirmDeleteImageModal.value = false;
		}
	};

	const text = ref<string>('');
	const typeCommentComputed = computed(() => {
		return Object.values(TYPE_COMMENT);
	});
	const importance = ref(typeCommentComputed.value[0] || null);

	const errorField = ref({
		text: { status: false, msg: '' }
	});

	const clearError = (type: string) => {
		errorField.value = { ...errorField.value, [type]: { status: false } };
	};

	const validateHandler = () => {
		const arrayRules: Array<{
			field: string;
			value: string | null;
			rules: VALIDATE_RULES[];
		}> = [{ field: 'text', value: text.value, rules: [VALIDATE_RULES.required] }];
		arrayRules.forEach((item) => {
			errorField.value = { ...errorField.value, [item.field]: validator(item.value, item.rules)[0] };
		});
	};

	const uploadNewFile = async (files: File[]) => {
		const response = await tripCustomsPhotoStore.tripCustomPhotoUpload({ tripId: props.currentTrip?.id, files: files });

		if (response.status === 'success') {
			getTalonPhoto();
		}
	};

	const saveHandler = () => {
		validateHandler();
		if (!Object.values(errorField.value).find((item) => item.status)) {
			emits('save', {
				cargoId: props.currentCargo.id,
				text: text.value,
				importance: importance.value?.id
			});
		}
	};

	const getTalonPhoto = async () => {
		loading.getTalonPhoto = true;
		const response = await tripCustomsPhotoStore.getByTrip({ tripId: props.currentTrip?.id });

		if (response.status === 'success') {
			tripTalonList.value = response.data;
		}

		loading.getTalonPhoto = false;
	};

	getTalonPhoto();
</script>

<style scoped lang="scss">
	.images {
		display: flex;
		flex-wrap: wrap;
		gap: 15px;
		overflow: auto;
		max-height: 180px;
		padding: 10px 12px;
		margin-top: 12px;
		margin-bottom: 10px;
		background-color: #fff;
		border-radius: 12px;

		.image {
			position: relative;
			width: 76px;
			height: 76px;
			min-width: 76px;
			background-repeat: no-repeat;
			background-position: center;
			background-size: cover;
			border-radius: 12px;
			box-shadow: 0px 0px 5.6px 0px #0000001f;
			margin: 2px 0px;

			img {
				width: 76px;
				height: 76px;
				border-radius: 12px;
			}

			.btn_remove {
				position: absolute;
				top: -9px;
				right: -9px;
			}
		}

		.loader-wrapper {
			display: flex;
			justify-content: center;
			align-items: center;
			width: 76px;
			height: 76px;

			.ui-loader {
				width: 100%;
				height: 100%;
				margin: auto;
			}
		}
	}

	.added-photo {
		display: flex;
		gap: 4px;
		align-items: center;
	}
	.loader {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		height: 100%;
	}
	.notification-empty {
		display: flex;
		align-items: center;
		justify-content: center;
		height: 100%;
		font-size: 14px;
		font-weight: 700;
		color: var(--color_font_black_wite);
	}
	.btn-read-all {
		position: relative;
		margin-left: auto;
		color: #2e4a85;
		font-size: 10px;
		font-weight: 500;
		text-decoration: dotted;
		cursor: pointer;

		&:hover {
			&::after {
				content: '';
				position: absolute;
				left: 0;
				bottom: 0;
				width: 100%;
				height: 1px;
				border-bottom: 1px solid #2e4a85;
			}
		}
	}
	.notification__body {
		.message {
			background-color: #eaedf5;
			word-break: break-word;
			padding: 8px 14px;
			border-radius: 12px;
			margin-top: 8px;
		}
	}
	.status {
		width: 9px;
		height: 9px;
		border-radius: 50%;
		background-blend-mode: overlay;
		background-color: #14ab87;

		&.--is-read {
			background: linear-gradient(260.49deg, rgba(255, 255, 255, 0) -0.38%, rgba(255, 255, 255, 0.4) 153.72%);
			background: #37547b4d;
		}
	}
	.notifications {
		display: flex;
		flex-direction: column;
		gap: 10px;
		height: 100%;
		padding: 12px 13px;
		background-color: #eaedf5;
		border-radius: 12px;
		height: 100%;
		min-height: max-content;
	}
	.notification {
		width: 100%;
		background-color: var(--white);
		padding: 15px;
		border-radius: 16px;

		.notification__header {
			display: flex;
			align-items: center;
			gap: 4px;
			justify-content: space-between;

			.status-wrapper {
				display: flex;
				align-items: center;
				gap: 4px;
			}
		}
	}
	.modal__mobile {
		display: flex;
		flex-direction: column;

		.ui-select__item {
			display: flex;
			align-items: center;

			.icon {
				margin-right: 5px;
			}
		}

		.label-select {
			margin-left: auto;
		}

		.upper-limit-clue {
			max-width: 300px;
			@media (max-width: $sm) {
				max-width: 200px;
			}
		}

		.input__secret {
			display: flex;
			flex-direction: column;
			gap: 8px;
			margin-bottom: -10px;
		}

		.wrapper {
			display: flex;
			flex-direction: column;
			overflow: auto;
			gap: 17px;
			margin-top: 14px;
			padding-right: 10px;
			margin-right: -10px;
			padding-bottom: 5px;
			padding-top: 5px;
			height: 100%;

			.talon-wrapper {
				width: 100%;
				background-color: #eaedf5;
				border-radius: 12px;
				padding: 12px 13px;
			}
		}
	}
</style>
